import presizeBackend from '../../services/presize-backend';
import presizeBackendRest from '../../services/presize-backend-rest';
import { isDbMigrated } from '../../lib/rest-feature-flag';
import { SizingStore } from './sizing-store';
import { runInAction } from 'mobx';
import { SizingGroup } from '../../models/sizing-group';
import { ApplyReviewGroupInput } from '../../models/request';
import { getPreselectedSizeChartId } from '../../lib/article-utils';

export class ToggleGroupReviewAction {
  private async setReviewValue(this: SizingStore, sizingGroup: SizingGroup, value: boolean): Promise<void> {
    // REST path (isDbMigrated=true): pure state update. PATCH the group's articles to set
    // is_reviewed, then regenerate groups (is_reviewed is part of the group key). Sizing is
    // unchanged, so groups are remapped without re-fetching sizing (no loadSizingRest).
    if (isDbMigrated()) {
      return this.setReviewValueRest(sizingGroup, value);
    }

    const sizingForGroup: ApplyReviewGroupInput[] = [];
    if (value && sizingGroup.selectedSizeKey && sizingGroup.selectedSizeChart) {
      const { commodityGroup, groupKey, selectedSizeKey, selectedSizeChart, sizingNote, sizeStructure, sizing } =
        sizingGroup;
      sizingForGroup.push({
        sizingGroup: {
          groupKey,
          commodityGroupCode: commodityGroup.code,
          preselectedSizeChartId: getPreselectedSizeChartId(sizingGroup),
        },
        sizeChartCode: selectedSizeChart.sizeChartCode,
        lengthChartCode: selectedSizeChart.lengthChartCode,
        sizeKeyId: selectedSizeKey.sizeKeyId,
        sizeKey: selectedSizeKey.sizeKey,
        sizeList: selectedSizeKey.sizeList,
        lengthList: selectedSizeKey.lengthList,
        sizeLengthSeparator: selectedSizeKey.sizeLengthSeparator,
        manualSizeKey: selectedSizeKey.manualSizeKey,
        manualSizeList: selectedSizeKey.manualSizeKey ? selectedSizeKey.sizeList : [],
        manualLengthList: selectedSizeKey.manualSizeKey ? selectedSizeKey.lengthList : [],
        manualSizeLengthSeparator: selectedSizeKey.manualSizeKey ? selectedSizeKey.sizeLengthSeparator : undefined,
        distribution: selectedSizeKey.distribution,
        distributionType: selectedSizeKey.distributionType,
        recoStrategy: selectedSizeKey?.recommendationStrategy,
        recoLevel: selectedSizeKey?.recommendationLevel,
        resetManualDistribution: false,
        referenceBrandCode: sizing?.referenceBrandCode,
        sizingNote,
        sizeStructure,
      });
    }
    await presizeBackend.applyReview(
      this.referenceId,
      [{ groupKey: sizingGroup.groupKey, reviewed: value }],
      sizingForGroup
    );
    runInAction(() => (sizingGroup.isReviewed = value));
  }

  /**
   * Group-level review toggle via REST (isDbMigrated=true, PT-57317).
   *
   * Two-step flow:
   *   1. PATCH /api/orders/{order_code}/articles with this group's article reference IDs and
   *      is_reviewed (Content-Type: application/merge-patch+json). No sizing payload is sent.
   *   2. POST /api/orders/{order_code}/article-groups to regenerate groups (is_reviewed is
   *      part of the group key), preserving sizing state (no loadSizingRest).
   *
   * Error handling: if the PATCH throws, the error propagates and the regroup (POST) is not
   * called, leaving the store untouched.
   */
  private async setReviewValueRest(this: SizingStore, sizingGroup: SizingGroup, value: boolean): Promise<void> {
    // Already at the target value (e.g. box flows calling markAsNotReviewed on an
    // already-unreviewed group) — nothing to persist, avoid a needless regroup.
    if (sizingGroup.isReviewed === value) {
      return;
    }

    await presizeBackendRest.updateArticles(
      this.referenceId,
      {
        article_reference_ids: sizingGroup.articles.map((article) => article.referenceId),
        is_reviewed: value,
      },
      undefined,
      'application/merge-patch+json'
    );

    // Optimistically reflect the new review state on the group (the source of truth) so the
    // regroup request produces the correct new group keys. regroupPreservingSizing() syncs
    // the per-article reviewed flags from group.isReviewed before POSTing.
    runInAction(() => (sizingGroup.isReviewed = value));

    await this.regroupPreservingSizing();
  }

  async toggleReview(this: SizingStore, sizingGroup: SizingGroup): Promise<void> {
    await this.setReviewValue(sizingGroup, !sizingGroup.isReviewed);
  }

  async markAsNotReviewed(this: SizingStore, sizingGroup: SizingGroup): Promise<void> {
    await this.setReviewValue(sizingGroup, false);
  }
}
